import { ClassChangeTip, HoverTip, weightChangeText } from "@/components/race/ClassChangeTip";
import { fmtNum, raceTime } from "@/lib/api";

const HeadItem = ({ label, value, accent, testId }) => (
  <div className="min-w-0" data-testid={testId}>
    <div className="eyebrow">{label}</div>
    <div className={`mt-0.5 font-display text-lg font-semibold truncate ${accent || "text-white"}`}>{value || "—"}</div>
  </div>
);

export const ClassScoreStrip = ({ cs, prefix }) => (
  <div className="flex flex-wrap items-center gap-x-6 gap-y-2 bg-[#0F172A] border border-slate-800 rounded px-4 py-2.5" data-testid={`${prefix}-class-score`}>
    <div>
      <div className="eyebrow">Track</div>
      <div className="text-sm text-white" data-testid={`${prefix}-category`}>{cs.category}</div>
    </div>
    <div>
      <div className="eyebrow">Day</div>
      <div className="text-sm text-white">{cs.day || "—"}{cs.holiday && <span className="text-amber-300 text-xs ml-1">({cs.holiday})</span>}</div>
    </div>
    <div>
      <div className="eyebrow">Age</div>
      <div className="text-sm text-white" data-testid={`${prefix}-age`}>{cs.age || "—"}</div>
    </div>
    <div>
      <div className="eyebrow">Table Used</div>
      <div className="text-sm text-white" data-testid={`${prefix}-table-used`}>{cs.table || "—"}</div>
    </div>
    <div>
      <div className="eyebrow">Class Matched</div>
      <div className="text-sm text-white">{cs.class_label || <span className="text-rose-300">No match</span>}</div>
    </div>
    <div className="ml-auto text-right">
      <div className="eyebrow">Class Score</div>
      <div className="font-display text-3xl font-bold text-emerald-400 leading-none" data-testid={`${prefix}-score`}>{cs.score ?? "—"}</div>
    </div>
  </div>
);

export const RaceFieldHeading = ({ race, prefix, showClass = false }) => (
  <div className="space-y-3">
  <div className="grid grid-cols-2 md:grid-cols-5 gap-x-6 gap-y-3">
    <HeadItem label="Venue" value={`${race.venue || "Unknown"} R${race.race_number ?? "?"}`} testId={`${prefix}-venue`} />
    <HeadItem label="Class" value={race.race_class} testId={`${prefix}-class`} />
    <HeadItem label="Distance" value={race.distance && `${fmtNum(race.distance, 0)}m`} testId={`${prefix}-distance`} />
    <HeadItem label="Field" value={`${race.evaluation?.field_size ?? (race.horses || []).filter((h) => !h.scratched).length} runners`} testId={`${prefix}-field-size`} />
    <HeadItem
      label={`Time (local)${race.start_aest && race.tz !== "AEST" ? ` • ${race.start_aest} AEST` : ""}`}
      value={raceTime(race)}
      accent="text-amber-400"
      testId={`${prefix}-time`}
    />
  </div>
  {showClass && race.class_score && <ClassScoreStrip cs={race.class_score} prefix={prefix} />}
  </div>
);

const shortDate = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(2, 4)}` : "");

const LastStartCells = ({ ls, today, weight, testId }) => {
  if (!ls) return <td colSpan={7} className="px-2 py-2 text-xs text-slate-600" data-testid={testId}>No official start</td>;
  const cs = ls.class_score || {};
  return (
    <>
      <td className="px-2 py-2 num text-xs whitespace-nowrap" data-testid={`${testId}-date`}>
        <span className="text-slate-200">{cs.day ? cs.day.slice(0, 3) : ""}</span> <span className="text-slate-500">{shortDate(ls.date)}</span>
      </td>
      <td className="px-2 py-2 whitespace-nowrap text-slate-200" title={`${ls.venue_code || ""} • ${cs.category || ""}`} data-testid={`${testId}-venue`}>
        {ls.venue}
        <span className="ml-1 text-[10px] text-slate-500 uppercase">{cs.category ? cs.category.slice(0, 4) : ""}</span>
      </td>
      <td className="px-2 py-2 whitespace-nowrap text-slate-300 text-xs" title={`RA code: ${ls.class_raw || ""}`}>
        {ls.race_class || (cs.class_label ? `${ls.class_raw || "HCP"} → Open` : ls.class_raw) || "—"}{{ "Two-Years-Old": " (2YO)", "Three-Years-Old": " (3YO)" }[ls.conditions] || ""}
      </td>
      <td className="px-2 py-2 num text-right" data-testid={`${testId}-weight`}>
        {ls.weight != null ? <HoverTip text={weightChangeText(weight, ls)} testId={`${testId}-weight-tip`}>{fmtNum(ls.weight, 1)}</HoverTip> : "—"}
      </td>
      <td className="px-2 py-2 num text-right whitespace-nowrap" data-testid={`${testId}-margin`}>
        {ls.finish === 1 ? <span className="text-emerald-400">Won</span> : ls.margin != null ? `${fmtNum(ls.margin, 2)}L` : "—"}
      </td>
      <td className="px-2 py-2 num text-right text-amber-300" data-testid={`${testId}-sp`}>{ls.sp != null ? `$${fmtNum(ls.sp, 2)}` : "—"}</td>
      <td className="px-2 py-2 num text-right font-bold text-emerald-400" data-testid={`${testId}-score`}>
        {cs.score != null ? <ClassChangeTip today={today} ls={ls} testId={`${testId}-class-tip`}>{cs.score}</ClassChangeTip> : "—"}
      </td>
    </>
  );
};

export const RaceFieldTable = ({ horses, prefix, todayScore }) => {
  const runners = horses.filter((h) => !h.scratched).sort((a, b) => (a.number ?? 99) - (b.number ?? 99));
  const out = horses.length - runners.length;
  return (
    <div className="scroll-x">
      <table className="w-full text-sm" data-testid={`${prefix}-table`}>
        <thead>
          <tr className="eyebrow">
            <th colSpan={7} className="px-3 pt-2 pb-1 font-normal text-left text-emerald-400 border-b border-emerald-500/30">This Start</th>
            <th className="w-3" />
            <th colSpan={7} className="px-3 pt-2 pb-1 font-normal text-left text-amber-400 border-b border-amber-500/30">Last Start (official race)</th>
          </tr>
          <tr className="text-left eyebrow border-b border-slate-800">
            <th className="px-2 py-2 font-normal w-12">No.</th>
            <th className="px-2 py-2 font-normal">Horse</th>
            <th className="px-2 py-2 font-normal">Last Form</th>
            <th className="px-2 py-2 font-normal text-right">Barrier</th>
            <th className="px-2 py-2 font-normal">Jockey</th>
            <th className="px-2 py-2 font-normal text-right">Weight</th>
            <th className="px-2 py-2 font-normal whitespace-nowrap">Record</th>
            <th />
            <th className="px-2 py-2 font-normal">Date</th>
            <th className="px-2 py-2 font-normal">Venue</th>
            <th className="px-2 py-2 font-normal">Class</th>
            <th className="px-2 py-2 font-normal text-right">Wt</th>
            <th className="px-2 py-2 font-normal text-right whitespace-nowrap">Beaten</th>
            <th className="px-2 py-2 font-normal text-right">SP</th>
            <th className="px-2 py-2 font-normal text-right whitespace-nowrap">Class Score</th>
          </tr>
        </thead>
        <tbody>
          {runners.map((h, i) => (
            <tr key={`${h.number}-${h.name}`} className="row-hover border-b border-slate-800/60" data-testid={`${prefix}-row-${i}`}>
              <td className="px-2 py-2 num text-emerald-400 font-bold">{h.number ?? "-"}</td>
              <td className="px-2 py-2 text-white font-medium whitespace-nowrap">{h.name}</td>
              <td className="px-2 py-2 num tracking-wider text-cyan-300">{h.form || "—"}</td>
              <td className="px-2 py-2 num text-right">{h.barrier != null ? fmtNum(h.barrier, 0) : "—"}</td>
              <td className="px-2 py-2 text-slate-300 whitespace-nowrap">{h.jockey || "—"}</td>
              <td className="px-2 py-2 num text-right">{h.weight != null ? `${fmtNum(h.weight, 1)}kg` : "—"}</td>
              <td className="px-2 py-2 num whitespace-nowrap text-slate-300" data-testid={`${prefix}-row-${i}-record`}>
                {h.career_starts != null ? `${h.career_starts} runs ${h.career_wins ?? 0} wins` : "—"}
              </td>
              <td />
              <LastStartCells ls={h.last_start} today={todayScore} weight={h.weight} testId={`${prefix}-row-${i}-ls`} />
            </tr>
          ))}
        </tbody>
      </table>
      {out > 0 && <p className="text-xs text-slate-500 mt-2 px-3">{out} scratched runner(s) not shown</p>}
    </div>
  );
};
